// Граница ошибок: превращает падение React-дерева в понятный экран с выходом.
//
// Без неё любое исключение в render/useEffect размонтирует всё дерево, и
// человек получает белый экран без единой подсказки — ровно то, ради чего
// заведён этот файл. Границ две штуки по назначению:
//   * вокруг всего приложения (main.tsx) — последний рубеж;
//   * вокруг каждой панели (Grid.tsx) — падение одной сессии не должно гасить
//     соседние окна, где человек прямо сейчас разговаривает с агентом.
//
// Стили здесь inline, а не в .css: этот экран обязан нарисоваться даже если
// стилевой чанк не доехал — а именно так выглядит половина «белых экранов».
// Цвета берём из токенов через var() с запасным значением на тот же случай.

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { reportError, resetAndReload } from '../lib/report'

interface Props {
  children: ReactNode
  /** Что именно упало — попадает в текст экрана и в отчёт на сервер. */
  scope?: string
  /** Компактная раскладка для одной панели внутри сетки. */
  compact?: boolean
  /** Закрыть упавший слот, не трогая сессию на сервере (только для панели). */
  onClose?: () => void
}

interface State {
  error: Error | null
}

const button = (primary: boolean) => ({
  appearance: 'none' as const,
  minHeight: 40,
  padding: '9px 16px',
  borderRadius: 'var(--r-md, 8px)',
  border: `1px solid var(--line)`,
  background: primary ? 'var(--panel)' : 'transparent',
  color: primary ? 'var(--fg)' : 'var(--soft)',
  font: 'inherit',
  fontSize: 13,
  cursor: 'pointer',
})

export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // componentStack (в каком компоненте рвануло) в сам Error не попадает,
    // а без него минифицированный стек из прода почти ничего не говорит.
    reportError(error, `react:${this.props.scope ?? 'app'}`, info.componentStack)
  }

  render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    const { scope, compact, onClose } = this.props
    return (
      <div
        role="alert"
        style={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: compact ? 16 : 24,
          textAlign: 'center',
          background: 'var(--deep)',
          color: 'var(--fg)',
          overflow: 'auto',
        }}
      >
        <div style={{ fontSize: compact ? 14 : 16, fontWeight: 600 }}>
          {scope ? `${scope}: что-то сломалось` : 'панель сломалась'}
        </div>
        <pre
          style={{
            maxWidth: '100%',
            margin: 0,
            padding: '10px 12px',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md, 8px)',
            background: 'var(--field)',
            color: 'var(--soft)',
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            textAlign: 'left',
          }}
        >
          {error.message || String(error)}
        </pre>
        <div style={{ fontSize: 12, color: 'var(--dim)', maxWidth: '34em' }}>
          Ошибка уже уехала в лог сервера. Обычно спасает перезагрузка. Если окно
          падает снова — {onClose ? 'закройте его: ' : 'сбросьте кеш; '}
          {onClose
            ? 'беседа на сервере от этого не пострадает, её можно открыть заново из меню.'
            : 'оболочка панели могла закешироваться битой.'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
          <button style={button(true)} onClick={() => location.reload()}>
            перезагрузить
          </button>
          <button style={button(false)} onClick={() => void resetAndReload()}>
            сбросить кеш и войти заново
          </button>
          {onClose ? (
            <button style={button(false)} onClick={onClose}>
              закрыть окно
            </button>
          ) : null}
        </div>
      </div>
    )
  }
}
